import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, Copy, Check, MessageSquare, Mail, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const REVIEW_URL = process.env.REACT_APP_GOOGLE_REVIEW_URL;

const FIRST_NAME_PLACEHOLDER = "[Prénom]";
const PROJECT_PLACEHOLDER = "[cuisine / dressing / aménagement]";

const TEMPLATES = {
  whatsapp: `Bonjour ${FIRST_NAME_PLACEHOLDER} 👋

J'espère que vous profitez toujours pleinement de votre ${PROJECT_PLACEHOLDER} !

Je viens de lancer ma présence Google pour SAD et un petit avis de votre part m'aiderait énormément à me faire connaître auprès d'autres Réunionnais qui cherchent un courtier de confiance.

Ça ne prend que 30 secondes via ce lien : ${REVIEW_URL || "[votre lien Google sera ajouté ici]"}

Merci 1000 fois ! 🙏
Marc — SAD`,

  sms: `Bonjour ${FIRST_NAME_PLACEHOLDER}, ici Marc de SAD. Si vous êtes satisfait(e) de votre ${PROJECT_PLACEHOLDER}, un petit avis Google m'aiderait beaucoup à développer mon activité. 30 sec : ${REVIEW_URL || "[lien à venir]"} Merci ! 🙏`,

  email: `Objet : Votre avis sur SAD compte pour nous 🙏

Bonjour ${FIRST_NAME_PLACEHOLDER},

J'espère que tout va bien et que vous profitez toujours pleinement de votre ${PROJECT_PLACEHOLDER} réalisé(e) avec SAD.

Je me permets de vous écrire car je viens de créer ma fiche Google pour SAD - Solutions Agencement Décoration. Pour me faire connaître localement auprès d'autres Réunionnais qui cherchent un courtier de confiance, les avis Google sont essentiels.

Si vous avez 30 secondes, un petit retour de votre part m'aiderait énormément :

👉 ${REVIEW_URL || "[Lien Google avis – sera ajouté ici]"}

Pas besoin d'écrire un roman, quelques mots et quelques étoiles suffisent largement !

Encore un grand merci pour votre confiance.
Bien à vous,

Marc VALDES
SAD - Solutions Agencement Décoration
contact@sad-reunion.re · +262 692 71 45 05`,
};

export default function Reviews() {
  const [copied, setCopied] = useState(null);

  const copy = (key) => {
    navigator.clipboard.writeText(TEMPLATES[key]);
    setCopied(key);
    toast.success("Message copié !");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <main className="min-h-screen bg-stone-50">
      <div className="mx-auto max-w-4xl px-6 py-16 sm:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-orange-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          Retour à l'accueil
        </Link>

        <div className="mt-10 max-w-3xl">
          <p className="mb-4 text-xs uppercase tracking-[0.25em] text-orange-800">
            Votre avis compte
          </p>
          <h1 className="text-4xl font-light leading-[1.05] tracking-tight text-stone-900 sm:text-6xl">
            Quelques mots de vous valent
            <br />
            <span className="italic">tous les discours.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600 sm:text-lg">
            Si vous avez confié votre projet à SAD, votre retour aide d'autres
            Réunionnais à choisir en confiance un courtier en agencement local.
            <strong className="block mt-2 text-stone-900">
              30 secondes suffisent.
            </strong>
          </p>
        </div>

        {/* CTA principal */}
        <div className="mt-12 rounded-3xl border border-stone-200 bg-white p-8 shadow-sm sm:p-12">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-1 text-orange-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 fill-orange-400" strokeWidth={1.2} />
                ))}
              </div>
              <p className="mt-4 text-2xl font-light tracking-tight text-stone-900 sm:text-3xl">
                Laisser un avis Google
              </p>
              <p className="mt-2 max-w-md text-sm text-stone-600">
                Cliquez sur le bouton, connectez-vous à votre compte Google,
                attribuez des étoiles et écrivez quelques mots. C'est tout.
              </p>
            </div>

            {REVIEW_URL ? (
              <a
                href={REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-review-cta"
              >
                <Button
                  size="lg"
                  className="h-14 rounded-full bg-stone-900 px-8 text-base text-stone-50 hover:bg-orange-800"
                >
                  <Star className="mr-2 h-4 w-4 fill-stone-50" strokeWidth={1.5} />
                  Donner mon avis
                </Button>
              </a>
            ) : (
              <div
                data-testid="google-review-pending"
                className="rounded-2xl bg-stone-100 px-5 py-4 text-sm text-stone-600"
              >
                <p className="font-medium text-stone-900">Fiche en cours de validation</p>
                <p className="mt-1 text-xs text-stone-500">
                  Le lien sera actif dès que Google aura validé la fiche SAD
                  (sous quelques jours).
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Templates pour solliciter des avis */}
        <section className="mt-20">
          <p className="mb-3 text-xs uppercase tracking-[0.25em] text-orange-800">
            Espace pro — pour le gérant
          </p>
          <h2 className="text-3xl font-light tracking-tight text-stone-900 sm:text-4xl">
            Modèles à envoyer à vos clients
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-stone-600">
            Copiez le message qui vous arrange, remplacez{" "}
            <code className="rounded bg-stone-100 px-1.5 py-0.5 text-xs">{FIRST_NAME_PLACEHOLDER}</code>{" "}
            et <code className="rounded bg-stone-100 px-1.5 py-0.5 text-xs">{PROJECT_PLACEHOLDER}</code>{" "}
            par les vraies infos client, puis envoyez.
          </p>

          <div className="mt-8 space-y-6">
            <TemplateCard
              icon={<MessageSquare className="h-4 w-4" strokeWidth={1.5} />}
              label="WhatsApp"
              text={TEMPLATES.whatsapp}
              onCopy={() => copy("whatsapp")}
              copied={copied === "whatsapp"}
              testId="template-whatsapp"
            />
            <TemplateCard
              icon={<Smartphone className="h-4 w-4" strokeWidth={1.5} />}
              label="SMS court"
              text={TEMPLATES.sms}
              onCopy={() => copy("sms")}
              copied={copied === "sms"}
              testId="template-sms"
            />
            <TemplateCard
              icon={<Mail className="h-4 w-4" strokeWidth={1.5} />}
              label="Email"
              text={TEMPLATES.email}
              onCopy={() => copy("email")}
              copied={copied === "email"}
              testId="template-email"
            />
          </div>

          {!REVIEW_URL && (
            <div className="mt-8 rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm text-orange-900">
              <strong>Note&nbsp;:</strong> les modèles ci-dessus contiennent un
              placeholder pour le lien Google. Dès que votre fiche Google
              Business est validée, le lien sera ajouté automatiquement.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

const TemplateCard = ({ icon, label, text, onCopy, copied, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-8"
  >
    <div className="mb-4 flex items-center justify-between gap-4">
      <div className="inline-flex items-center gap-2 rounded-full bg-stone-100 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-stone-700">
        {icon}
        {label}
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onCopy}
        data-testid={`${testId}-copy`}
        className="rounded-full text-stone-600 hover:bg-stone-100 hover:text-stone-900"
      >
        {copied ? (
          <>
            <Check className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
            Copié
          </>
        ) : (
          <>
            <Copy className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
            Copier
          </>
        )}
      </Button>
    </div>
    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-stone-700">
      {text}
    </pre>
  </div>
);
